// src/context/CartContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // ================= LOAD FROM LOCALSTORAGE =================
  useEffect(() => {
    try {
      const saved = localStorage.getItem('cartItems');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setCartItems(parsed);
        }
      }
    } catch (error) {
      console.error('Error loading cart:', error);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  // ================= SAVE TO LOCALSTORAGE =================
  useEffect(() => {
    if (isLoaded) {
      try {
        localStorage.setItem('cartItems', JSON.stringify(cartItems));
      } catch (error) {
        console.error('Error saving cart:', error);
      }
    }
  }, [cartItems, isLoaded]);

  // ================= ADD TO CART =================
  const addToCart = (product, quantity = 1) => {
    setCartItems(prev => {
      const existingIndex = prev.findIndex(item => item.id === product.id);

      if (existingIndex > -1) {
        // Item already in cart — quantity update
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity,
        };
        return updated;
      } else {
        // New item
        return [
          ...prev,
          {
            id: product.id,
            title: product.title,
            price: Number(product.price),
            originalPrice: Number(product.originalPrice) || 0,
            image: product.image || (product.images && product.images[0]) || '',
            category: product.category || '',
            inventory: product.inventory || 0,
            quantity: quantity,
            addedAt: Date.now(),
          },
        ];
      }
    });
  };

  // ================= REMOVE FROM CART =================
  const removeFromCart = (productId) => {
    setCartItems(prev => prev.filter(item => item.id !== productId));
  };

  // ================= UPDATE QUANTITY =================
  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) return;

    setCartItems(prev =>
      prev.map(item =>
        item.id === productId
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  // ================= INCREMENT =================
  const incrementQuantity = (productId) => {
    setCartItems(prev =>
      prev.map(item =>
        item.id === productId
          ? { ...item, quantity: item.quantity + 1 }
          : item
      )
    );
  };

  // ================= DECREMENT =================
  const decrementQuantity = (productId) => {
    setCartItems(prev =>
      prev.map(item =>
        item.id === productId
          ? { ...item, quantity: Math.max(1, item.quantity - 1) }
          : item
      )
    );
  };

  // ================= CLEAR CART =================
  const clearCart = () => {
    setCartItems([]);
  };

  // ================= CHECK IF IN CART =================
  const isInCart = (productId) => {
    return cartItems.some(item => item.id === productId);
  };

  // ================= GET ITEM COUNT (Total Quantity) =================
  const getItemCount = () => {
    return cartItems.reduce((sum, item) => sum + item.quantity, 0);
  };

  // ================= GET TOTAL (Subtotal) =================
  const getSubtotal = () => {
    return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  // ================= GET TOTAL SAVINGS =================
  const getTotalSavings = () => {
    return cartItems.reduce((sum, item) => {
      if (item.originalPrice && item.originalPrice > item.price) {
        return sum + ((item.originalPrice - item.price) * item.quantity);
      }
      return sum;
    }, 0);
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    incrementQuantity,
    decrementQuantity,
    clearCart,
    isInCart,
    getItemCount,
    getSubtotal,
    getTotalSavings,
    isLoaded,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;